import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "~/utils/toast";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { FileUploadDialog } from "~/components/file-upload-dialog";
import { useManuscriptActions } from "~/hooks/use-manuscript-actions";
import {
  EmptyState,
  ManuscriptList,
  ManuscriptTeaser,
  PageHeader,
} from "./components";
import { useManuscripts } from "./hooks";

const UploadManuscriptContent = () => {
  const { t } = useTranslation(["uploadManuscript"]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { loading: isLoading, hasManuscripts } = useManuscripts();
  const { createProductFromManuscript, generateProductManuscriptAnalysis } =
    useManuscriptActions();

  const handleFileUpload = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];

      setIsUploading(true);
      createProductFromManuscript(file)
        .then((result) => {
          toast.success(t("uploadManuscript:toasts.success"));
          setDialogOpen(false);

          if (!result?.id) {
            toast.error(t("uploadManuscript:toasts.uploadError"));
            return;
          }

          generateProductManuscriptAnalysis(result.id).catch(() => {
            toast.error(t("uploadManuscript:toasts.analysisError"));
          });
        })
        .catch(() => {
          toast.error(t("uploadManuscript:toasts.uploadError"));
        })
        .finally(() => {
          setIsUploading(false);
        });
    },
    [t, createProductFromManuscript, generateProductManuscriptAnalysis],
  );

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader />
        <div className="animate-pulse h-32 bg-secondary-100 rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        onUploadClick={hasManuscripts ? handleOpenDialog : undefined}
      />

      {hasManuscripts ? (
        <ManuscriptList />
      ) : (
        <EmptyState onUploadClick={handleOpenDialog} />
      )}

      <FileUploadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onFileUpload={handleFileUpload}
        title={t("uploadManuscript:dialog.title")}
        description={t("uploadManuscript:dialog.description")}
        accept={{
          "application/pdf": [".pdf"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
        }}
        maxSizeMB={25}
        isLoading={isUploading}
      />
    </div>
  );
};

export const UploadManuscript = () => {
  const { features } = useFeatureFlags();

  if (features.uploadManuscript) {
    return <UploadManuscriptContent />;
  }

  if (features.uploadManuscriptTeaser) {
    return <ManuscriptTeaser />;
  }

  return null;
};
